import { inject, Injectable, NgZone } from '@angular/core';
import { DBStatus, DelStatus, MRproResponse } from 'src/app/types/api';
import { AppDB } from './dexieDB';
import { HilfeBeschreibung } from 'src/app/content/_components/_modals/hilfebeschreibungmodal/hilfebeschreibungmodal.component';
import { Table } from 'src/app/models/ui/table';
import {
  MessungModel,
  MesswertEintrag,
  MesswertErfassung,
  ToleranzenBezeichnungen,
  ZählerName,
  ZählerPrüfung,
  ZählerWert,
} from 'src/app/content/_components/_overlays/zaehlerverwaltung/zaehler.types';
import { GetDBStatus, AsMappedTableFE, AsMappedRows } from './sharedDB';
import { MessungDropdownList } from 'src/app/content/_components/_overlays/zaehlerverwaltung/zaehler.enums';
import { rgbToHex } from 'src/app/misc/functions';
import { ZählerConcatenated } from 'src/app/content/_components/detailview/zaehler-accordion/zaehler-accordion.component';

//#region Interfaces for DB Structure
export interface SZAEEH extends DBStatus, DelStatus {
  ID?: number;
  OTYPID: number;
  Code: string;
  Bezeichnung: string;
}
export interface SZAEHN extends DBStatus, HilfeBeschreibung, DelStatus {
  ID: number;
  OTYPID: number;
  Code: string;
  Bezeichnung: string;
  isSR100: boolean;
  isSRG: boolean;
  SourceType: 1 | 2;
  FATVOLID: number;
  Bild: string;
  Bemerkung: string;
  Track0: number;
  Track1: number;
  Track2: number;
  Track3: number;
}
export interface SZAEHW extends DBStatus, DelStatus {
  ID?: number;
  SZAEHNID: number;
  Code: string;
  Bezeichnung: string;
  Soll: number;
  SR100plus: number;
  SR100minus: number;
  aktivSR100: boolean;
  SRlimplus: number;
  SRlimminus: number;
  aktivSRlimm: boolean;
  SRGplus: number;
  SRGminus: number;
  aktivSRG: boolean;
  I_SORT: number;
  ZArt: number;
  ZType: number;
  ZEinheit: number;
  SGenau: number;
  TGenau: number;
}
export interface KTOLR extends DBStatus {
  ID: number;
  USE_0: boolean;
  USE_100: boolean;
  USE_L: boolean;
  USE_G: boolean;
  TEXT_0_m: string;
  TEXT_0_p: string;
  TEXT_100_m: string;
  TEXT_100_p: string;
  TEXT_L_m: string;
  TEXT_L_p: string;
  TEXT_G_m: string;
  TEXT_G_p: string;
  TEXT_0_A: string;
  TEXT_100_A: string;
  TEXT_L_A: string;
  TEXT_G_A: string;
  TEXT_0_S: string;
  TEXT_100_S: string;
  TEXT_L_S: string;
  TEXT_Ausfall_0: string;
  TEXT_Ausfall_I: string;
  TEXT_G_S: string;
  COLOR_0: string;
  COLOR_100: string;
  COLOR_L: string;
  COLOR_G: string;
}
export interface NMESS {
  ID?: number;
  Typ: number;
  Bezeichnung: string;
}
//#endregion

@Injectable({ providedIn: 'root' })
export class ZaehlerDB {
  private readonly db = inject(AppDB);
  private readonly zone = inject(NgZone);

  //#region Zählerwerte
  async GetZaehlerWerte(SZAEHNID: number): Promise<Table<ZählerWert>> {
    return await this.zone.runOutsideAngular(async () => {
      // SELECT szaehw.*, szaeeh.Code as Einheit FROM szaehw LEFT JOIN szaeeh ON szaeeh.ID = szaehw.ZEinheit WHERE szaehw.DelStatus = 0 AND SZAEHNID = ? ORDER BY I_SORT, szaehw.ID
      const werte = (
        await this.db.szaehw
          .where({ SZAEHNID })
          .and(x => x.DelStatus == 0)
          .sortBy('I_SORT')
      ).sort((a, b) => (a.I_SORT == b.I_SORT ? a.ID - b.ID : 0));

      const rows = await Promise.all(
        werte.map(async wert => {
          const einheit = await this.db.szaeeh.get(wert.ZEinheit);
          return { ...wert, Einheit: einheit?.Code };
        })
      );
      return AsMappedTableFE<ZählerWert>()(rows);
    });
  }
  //#endregion
  //#region Shared
  async GetToleranzenEinstellungen(): Promise<ToleranzenBezeichnungen> {
    const tol = await this.db.ktolr.get(1);
    if (!tol) return null;
    Object.keys(tol).forEach(key => {
      if (key == 'ID' || key.includes('_0') || key.includes('USE_')) delete tol[key];
      else if (key.includes('COLOR')) tol[key] = rgbToHex(tol[key]);
    });
    return tol;
  }
  //#endregion
  //#region Erfassung
  async GetZaehlerBySZAEHNID(id: number): Promise<ZählerName> {
    try {
      const name = await this.db.szaehn.get(id);
      return name;
    } catch (_) {
      return null;
    }
  }

  async GetZaehlerByObjektID(OSTAMMID: number, KPRUVID: number): Promise<ZählerName> {
    try {
      const ids = (await this.db.sxmkp.where({ OSTAMMID, KPRUVID }).toArray()).map(x => x.ZAEID_HP);

      for (const id of ids) {
        const name = await this.db.szaehn.get(id);
        if (!name.DelStatus && !name.FATVOLID) return name;
      }
    } catch (_) {
      return null;
    }
  }

  async GetZaehlerByMaskeID(mID: number): Promise<ZählerName> {
    try {
      const ids = (await this.db.kwamzn.where({ RevMaskenNameID: mID }).toArray()).map(x => x.SZAEHNID);

      for (const id of ids) {
        const name: SZAEHN = await this.db.szaehn.get(id);
        return name;
      }
    } catch {
      return null;
    }
  }

  async GetPrüfungenByObjektID(OSTAMMID: number): Promise<ZählerPrüfung[]> {
    try {
      const pruefungen = (
        await this.db.opruef
          .where({ OSTAMMID })
          .and(x => !x.P_DelStatus && !!x.P_ZDatum)
          .sortBy('P_ZDatum')
      ).sort((a, b) => (a.P_ZDatum == b.P_ZDatum ? a.ID - b.ID : 0));

      return pruefungen.map(pruefung => ({
        OPRUEFID: pruefung.OPRUEFID,
        P_Bemerkung: pruefung.P_Bemerkung,
        P_Datum: pruefung.P_Datum,
        P_ZDatum: pruefung.P_ZDatum,
        P_ZUser: pruefung.P_ZUser,
        M_Device: pruefung.M_Device,
      }));
    } catch {
      return null;
    }
  }

  async GetZaehlerMessungByPruefungID(OPRUEFID: number, id: number): Promise<MesswertErfassung[]> {
    try {
      const messungen = await this.db.omess
        .where({ OPRUEFID })
        .and(x => x.SZAEHNID == id || x.ZTYP == id)
        .reverse()
        .sortBy('ID');

      return messungen.map(messung => ({
        ID: messung.ID,
        Bezeichnung: messung.Bezeichnung,
        Ist: messung.Ist,
        Ausfall: messung.Ausfall,
        Bemerkung: messung.Bemerkung,
      }));
    } catch {
      return null;
    }
  }

  async SetZaehlerMessung(OPRUEFID: number, SZAEHNID: number, req: MessungModel): Promise<MRproResponse> {
    try {
      const {
        pruefung: { OPRUEFID: _, Column_Datum: __, ...pruefung },
        messwerte,
      } = req;
      await this.db.transaction('rw', [this.db.opruef, this.db.omess, this.db.nmess], async () => {
        await this.db.opruef.where({ OPRUEFID }).modify({ ...pruefung, ...GetDBStatus() });
        await this.setZaehlerMessenderOrGerät(1, pruefung.P_ZUser);
        if (pruefung.M_Device) await this.setZaehlerMessenderOrGerät(2, pruefung.M_Device);

        for (const messwert of messwerte ?? []) {
          if (!messwert.ID) {
            delete messwert.ID;
            await this.db.omess.add({
              ...messwert,
              SZAEHNID,
              OPRUEFID,
              Messender: pruefung.P_ZUser,
              ...GetDBStatus(),
            });
          } else {
            await this.db.omess.update(messwert.ID, { ...messwert, Messender: pruefung.P_ZUser, ...GetDBStatus() });
          }
        }
      });
      return { success: true };
    } catch (er) {
      return { success: false, error: (er as Error).message };
    }
  }

  async GetZaehlerMessenderOrGerät(typ: MessungDropdownList): Promise<string[]> {
    try {
      const Typ =
        typ == MessungDropdownList.Messender ? 1
        : typ == MessungDropdownList.Messgerät ? 2
        : null;
      if (!Typ) throw new Error('Invalid Typ');

      const bezeichnungen = await this.db.nmess.where({ Typ }).toArray();
      return [...new Set(bezeichnungen.map(x => x.Bezeichnung))];
    } catch {
      return null;
    }
  }
  //#endregion
  //#region Übersicht
  async GetZaehlerMessungByOPRUEFID(OPRUEFID: number): Promise<MesswertEintrag[]> {
    try {
      const messwerte = await this.db.omess.where({ OPRUEFID }).reverse().sortBy('ID');
      return AsMappedRows<MesswertEintrag>()(messwerte);
    } catch (_) {
      return null;
    }
  }
  //#endregion
  async GetZaehlerPruefungen(OSTAMMID: number): Promise<Record<number, ZählerConcatenated>> {
    return await this.zone.runOutsideAngular(async () => {
      let zaehlerConcatenated: Record<number, ZählerConcatenated>;

      const pruefungen = await this.db.opruef
        .where({ OSTAMMID })
        .and(x => !x.P_DelStatus)
        .reverse()
        .sortBy('P_Datum');

      const zaehlerPruefungen = await Promise.all(
        pruefungen.map(async ({ OPRUEFID, P_Datum }) => {
          const eintrag = await this.db.omess
            .where({ OPRUEFID })
            .and(x => x.SZAEHNID > 0 || x.ZTYP > 0)
            .first();
          if (!eintrag) return null;

          const SZAEHNID = eintrag.SZAEHNID || eintrag.ZTYP;
          return { SZAEHNID, OPRUEFID, P_Datum };
        })
      );

      for (const eintrag of zaehlerPruefungen) {
        if (!eintrag) continue;
        const { SZAEHNID, OPRUEFID, P_Datum } = eintrag;

        zaehlerConcatenated ??= {};
        if (!zaehlerConcatenated[SZAEHNID]) {
          const Code = (await this.db.szaehn.get(SZAEHNID)).Code ?? '---';
          zaehlerConcatenated[SZAEHNID] = { SZAEHNID, Code, Pruefungen: `${OPRUEFID}=${P_Datum}` };
        } else {
          const concatenated = zaehlerConcatenated[SZAEHNID];
          concatenated.Pruefungen = `${concatenated.Pruefungen}|${OPRUEFID}=${P_Datum}`;
        }
      }

      return zaehlerConcatenated;
    });
  }

  private async setZaehlerMessenderOrGerät(Typ: 1 | 2, Bezeichnung: string) {
    try {
      if (!(await this.db.nmess.where({ Typ, Bezeichnung }).first())) await this.db.nmess.add({ Typ, Bezeichnung });
    } catch (error) {
      console.log(error);
    }
  }
}
